import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Card, Row, Col, ListGroup, Accordion } from 'react-bootstrap'
import { FaSearch, FaChevronDown, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { BsArrowUp } from 'react-icons/bs';
import './../styles.css';
import { NetworkProvider } from '../../NetworkProvider';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import PersonModal from './PersonModal';
import ErrorModal from './ErrorModal';
import EditPersonModal from './EditPersonModal';
import AuthBackground from '../AuthForm/AuthBackground';

import { useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';

export default function Home() {

  const [nameSearch, setNameSearch] = useState('');
  const [surnameSearch, setSurnameSearch] = useState('');
  const [birthdateSearch, setBirthdateSearch] = useState('');
  const [people, setPeople] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPersonModal, setShowPersonModal] = useState(null);

  // Changed to array to support multiple selections
  const [selectedTables, setSelectedTables] = useState([0]);
  const [showTopButton, setShowTopButton] = useState(false);
  const [showLoder, setShowLoader] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 20;

  // Sorting state: { field: 'name' | 'surname' | 'birthdate' | 'rank' | 'deathdate' | null, direction: 'asc' | 'desc' }
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });

  const totalPages = Math.ceil(people.length / itemsPerPage);

  const paginatedPeople =
    people.length <= itemsPerPage
      ? people
      : people.slice(
        currentPage * itemsPerPage,
        currentPage * itemsPerPage + itemsPerPage
      );

  const [dienestaVienibaSearch, setDienestaVienibaSearch] = useState('');

  const handleDienestaVienibaSearch = (event) => setDienestaVienibaSearch(event.target.value);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user, userReady } = useUser();
  const isStaff = user?.is_staff;

  useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [userReady]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowTopButton(true);
      } else {
        setShowTopButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if birthdate search should be enabled based on selected tables
  const isBirthdateSearchEnabled = selectedTables.length === 0 || selectedTables.includes(0) || selectedTables.some(table => [2, 3, 4, 5, 6].includes(table));

  const databaseGroups = {
    wwi: [
      { label: t("Latviešu strēlnieku bataljonos (pulkos) dienējušo saraksts"), value: 5 },
      { label: t("Kritušo, bez vēsts pazudušo, ievainoto un gūstā nonākušo latviešu strēlnieku saraksts"), value: 3 },
      { label: t("Latviešu strēlnieku 2. brigādes apbalvoto karavīru saraksts"), value: 1 }
    ],
    wwii: [
      { label: t("Latviešu SS leģionā mobilizēto saraksts"), value: 2 },
      { label: t("Zedelgemas (Beļģija) karagūstekņu nometnē ieslodzīto latviešu karavīru saraksts"), value: 4 },
      { label: t("Kritušo un bez vēsts pazudušo Latviešu SS leģiona karavīru saraksts"), value: 6 }
    ]
  };

  const handleTableToggle = (tableValue) => {
    if (tableValue === 0) {
      // Toggle "Search All"
      setSelectedTables(prev => {
        if (prev.includes(0)) {
          // If "Search All" is currently selected, deselect it and select nothing
          return [];
        } else {
          // Select "Search All"
          return [0];
        }
      });
    } else {
      setSelectedTables(prev => {
        // Remove "Search All" if it's selected
        const withoutSearchAll = prev.filter(t => t !== 0);

        if (withoutSearchAll.includes(tableValue)) {
          // Uncheck this table
          const newSelection = withoutSearchAll.filter(t => t !== tableValue);
          // If nothing selected, return empty array (don't auto-select "Search All")
          return newSelection;
        } else {
          // Check this table
          return [...withoutSearchAll, tableValue];
        }
      });
    }
  };

  // Sorting function
  const handleSort = (field) => {
    setSortConfig(prevConfig => {
      // If clicking the same field, toggle direction
      if (prevConfig.field === field) {
        return {
          field,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // If clicking a new field, set it to ascending
      return { field, direction: 'asc' };
    });
  };

  // Get sort icon for a field
  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" /> 
      : <FaSortDown className="ms-1" />;
  };

  // Apply sorting to people array
  const getSortedPeople = () => {
    if (!sortConfig.field) return people;

    const latvianAlphabet = [
      'a', 'ā', 'b', 'c', 'č', 'd', 'e', 'ē', 'f', 'g', 'ģ', 'h',
      'i', 'ī', 'j', 'k', 'ķ', 'l', 'ļ', 'm', 'n', 'ņ', 'o', 'p',
      'r', 's', 'š', 't', 'u', 'ū', 'v', 'z', 'ž'
    ];

    const sorted = [...people].sort((a, b) => {
      let valueA, valueB;

      if (sortConfig.field === 'name') {
        valueA = (a.name || '').toLowerCase();
        valueB = (b.name || '').toLowerCase();
      } else if (sortConfig.field === 'surname') {
        valueA = (a.surname || '').toLowerCase();
        valueB = (b.surname || '').toLowerCase();
      } else if (sortConfig.field === 'rank') {
        valueA = (a.rank || a.first_rank || '').toLowerCase();
        valueB = (b.rank || b.first_rank || '').toLowerCase();
      } else if (sortConfig.field === 'birthdate') {
        // Extract year from birthdate
        valueA = parseInt((a.birthdate || '').replace(/\D/g, '')) || 0;
        valueB = parseInt((b.birthdate || '').replace(/\D/g, '')) || 0;
        
        // For numeric sorting
        const comparison = valueA - valueB;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      } else if (sortConfig.field === 'deathdate') {
        // Extract year from death date (checking multiple possible fields)
        const deathA = a.death_date || a.other_death_date || a.dieddate || '';
        const deathB = b.death_date || b.other_death_date || b.dieddate || '';
        valueA = parseInt(deathA.replace(/\D/g, '')) || 0;
        valueB = parseInt(deathB.replace(/\D/g, '')) || 0;
        
        // For numeric sorting
        const comparison = valueA - valueB;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Latvian alphabetic sorting for name, surname, and rank
      if (sortConfig.field === 'name' || sortConfig.field === 'surname' || sortConfig.field === 'rank') {
        for (let i = 0; i < Math.max(valueA.length, valueB.length); i++) {
          const letterA = valueA[i] || '';
          const letterB = valueB[i] || '';

          const indexA = latvianAlphabet.indexOf(letterA);
          const indexB = latvianAlphabet.indexOf(letterB);

          if (indexA !== indexB) {
            const comparison = indexA - indexB;
            return sortConfig.direction === 'asc' ? comparison : -comparison;
          }
        }
      }

      return 0;
    });

    return sorted;
  };

  const fetchPeople = async () => {
    try {
      setShowLoader(true);
      setPeople([]);
      if (
        (nameSearch.trim().length > 0 && nameSearch.trim().length < 3) ||
        (surnameSearch.trim().length > 0 && surnameSearch.trim().length < 3) ||
        (dienestaVienibaSearch.trim().length > 0 && dienestaVienibaSearch.trim().length < 3)
      ) {
        setIsSearching(false);
        setShowLoader(false);
        return;
      }

      if (
        !nameSearch.trim() &&
        !surnameSearch.trim() &&
        !birthdateSearch.trim() &&
        !dienestaVienibaSearch.trim()
      ) {
        setIsSearching(false);
        setShowLoader(false);
        return;
      }

      // Convert selected tables array to comma-separated string
      // If no tables selected, default to searching all (0)
      const tableParam = selectedTables.length === 0 ? '0' : selectedTables.join(',');
      const networkSearchResults = await NetworkProvider.search(tableParam, nameSearch, surnameSearch, birthdateSearch, dienestaVienibaSearch)

      const results = networkSearchResults.map((item, index) => ({
        ...item,
        id: index + 1
      }));

      const latvianAlphabet = [
        'a', 'ā', 'b', 'c', 'č', 'd', 'e', 'ē', 'f', 'g', 'ģ', 'h',
        'i', 'ī', 'j', 'k', 'ķ', 'l', 'ļ', 'm', 'n', 'ņ', 'o', 'p',
        'r', 's', 'š', 't', 'u', 'ū', 'v', 'z', 'ž'
      ];

      const latvianSort = (a, b) => {
        const surnameA = (a.surname || '').toLowerCase();
        const surnameB = (b.surname || '').toLowerCase();

        for (let i = 0; i < Math.max(surnameA.length, surnameB.length); i++) {
          const letterA = surnameA[i] || '';
          const letterB = surnameB[i] || '';

          const indexA = latvianAlphabet.indexOf(letterA);
          const indexB = latvianAlphabet.indexOf(letterB);

          if (indexA !== indexB) {
            return indexA - indexB;
          }
        }

        const firstNameA = (a.name || '').toLowerCase();
        const firstNameB = (b.name || '').toLowerCase();

        for (let i = 0; i < Math.max(firstNameA.length, firstNameB.length); i++) {
          const letterA = firstNameA[i] || '';
          const letterB = firstNameB[i] || '';

          const indexA = latvianAlphabet.indexOf(letterA);
          const indexB = latvianAlphabet.indexOf(letterB);

          if (indexA !== indexB) {
            return indexA - indexB;
          }
        }

        return 0;
      };

      const sortedPeople = results.sort(latvianSort);

      setPeople(sortedPeople);
      setIsSearching(true);
      setShowLoader(false);
      setCurrentPage(0);
      // Reset sort when new search is performed
      setSortConfig({ field: null, direction: 'asc' });
    } catch (error) {
      setError(error.message || 'An error occurred');
      setIsSearching(true);
      setShowLoader(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNameSearch = (event) => {
    const searchString = event.target.value.replace(/\d+/g, '');
    setNameSearch(searchString);
  }

  const handleSurnameSearch = (event) => {
    const searchString = event.target.value.replace(/\d+/g, '');
    setSurnameSearch(searchString);
  }

  const handleBirthdateSearch = (event) => {
    // Only allow numbers and limit to 4 digits
    const value = event.target.value.replace(/\D/g, '').slice(0, 4);
    setBirthdateSearch(value);
  };

  const onEdited = async (record) => {
    try {
      setShowLoader(true);
      await NetworkProvider.update(record)
      const updatedPeople = people.map(person =>
        person.id === record.id ? record : person
      );
      setPeople(updatedPeople)
      setShowLoader(false);
    } catch (error) {
      setError(error.message);
      setShowLoader(false);
    }
  }

  const onNextPerson = () => {
    if (!selectedPerson) return;

    // Use sorted people for navigation
    const sortedPeopleList = getSortedPeople();
    const currentIndex = sortedPeopleList.findIndex(person => person.id === selectedPerson.id);

    if (currentIndex !== -1 && currentIndex < sortedPeopleList.length - 1) {
      const nextPerson = sortedPeopleList[currentIndex + 1];
      setSelectedPerson(nextPerson);

      const nextPage = Math.floor((currentIndex + 1) / itemsPerPage);
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      }
    }
  };

  const onPreviousPerson = () => {
    if (!selectedPerson) return;

    // Use sorted people for navigation
    const sortedPeopleList = getSortedPeople();
    const currentIndex = sortedPeopleList.findIndex(person => person.id === selectedPerson.id);

    if (currentIndex > 0) {
      const previousPerson = sortedPeopleList[currentIndex - 1];
      setSelectedPerson(previousPerson);

      const previousPage = Math.floor((currentIndex - 1) / itemsPerPage);
      if (previousPage !== currentPage) {
        setCurrentPage(previousPage);
      }
    }
  };

  // Helper functions to check if next/previous exist
  const hasNextPerson = () => {
    if (!selectedPerson) return false;
    const sortedPeopleList = getSortedPeople();
    const currentIndex = sortedPeopleList.findIndex(person => person.id === selectedPerson.id);
    return currentIndex !== -1 && currentIndex < sortedPeopleList.length - 1;
  };

  const hasPreviousPerson = () => {
    if (!selectedPerson) return false;
    const sortedPeopleList = getSortedPeople();
    const currentIndex = sortedPeopleList.findIndex(person => person.id === selectedPerson.id);
    return currentIndex > 0;
  };

  const isSearchAllSelected = selectedTables.includes(0);

  // Get sorted and paginated people
  const sortedPeople = getSortedPeople();
  const displayedPeople = sortedPeople.length <= itemsPerPage
    ? sortedPeople
    : sortedPeople.slice(
      currentPage * itemsPerPage,
      currentPage * itemsPerPage + itemsPerPage
    );

  return (
    <Container className="flex-grow-1 d-flex justify-content-center align-items-center py-1">
      <AuthBackground blurredForeground={true} />
      <Card className="p-4 shadow-lg w-100" style={{ border: 'none', maxWidth: 750 }}>
        <h3 className="text-start">{t("Karavīru meklēšana")}</h3>
        <br />

        {/* Database Selection with Checkboxes */}
        <Form.Group>
          <Form.Label className="fw-semibold">{t("Izvēlēties datubāzi")}</Form.Label>

          {/* Search All Option */}
          <ListGroup className="mb-4">
            <ListGroup.Item
              action
              onClick={() => handleTableToggle(0)}
              className="bg-light"
            >
              <Form.Check
                type="checkbox"
                id="table-all"
                label={<span className="fw-bold">{t("Meklēt visās")}</span>}
                checked={isSearchAllSelected}
                onChange={() => { }}
                readOnly
                style={{ pointerEvents: 'none' }}
              />
            </ListGroup.Item>
          </ListGroup>

          {/* WWI Accordion */}
          <Accordion defaultActiveKey={[]} alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <span className="fw-semibold">{t("Pirmais Pasaules Karš")}</span>
              </Accordion.Header>
              <Accordion.Body>
                {databaseGroups.wwi.map((db) => (
                  <Form.Check
                    key={db.value}
                    type="checkbox"
                    id={`table-${db.value}`}
                    label={db.label}
                    checked={isSearchAllSelected || selectedTables.includes(db.value)}
                    onChange={() => handleTableToggle(db.value)}
                    disabled={isSearchAllSelected}
                    className="mb-2"
                    style={{ gap: 10, display: 'flex', alignItems: 'center' }}

                  />
                ))}
              </Accordion.Body>
            </Accordion.Item>

            {/* WWII Accordion */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <span className="fw-semibold">{t("Otrais Pasaules Karš")}</span>
              </Accordion.Header>
              <Accordion.Body>
                {databaseGroups.wwii.map((db) => (
                  <Form.Check
                    key={db.value}
                    type="checkbox"
                    id={`table-${db.value}`}
                    label={db.label}
                    checked={isSearchAllSelected || selectedTables.includes(db.value)}
                    onChange={() => handleTableToggle(db.value)}
                    disabled={isSearchAllSelected}
                    className="mb-2"
                    style={{ gap: 10, display: 'flex', alignItems: 'center' }}
                  />
                ))}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Form.Group>

        <hr className="my-5 border-primary" />
        <Form onSubmit={(e) => {
          e.preventDefault();
          fetchPeople();
        }}>
          <Form.Group className="mb-4 w-100">
            <Form.Control
              type="search"
              placeholder={t("Meklēt pēc vārda")}
              value={nameSearch}
              onChange={handleNameSearch}
              className={`w-100 ${nameSearch.length > 0 && nameSearch.length < 3 ? "is-invalid" : ""}`}
              style={{
                border: nameSearch.length > 0 && nameSearch.length < 3 ? '1px solid red' :
                  nameSearch.length >= 3 ? '1px solid green' : '1px solid #ccc',
              }}
            />
            {nameSearch.length > 0 && nameSearch.length < 3 && (
              <div className="invalid-feedback d-block mb-3">
                {t("Vārda meklēšanai nepieciešami vismaz 3 burti")}
              </div>
            )}
            {nameSearch.length >= 3 && <div className="mb-3"></div>}
            {nameSearch.length === 0 && <div className="mb-4"></div>}

            <Form.Control
              type="search"
              placeholder={t("Meklēt pēc uzvārda")}
              value={surnameSearch}
              onChange={handleSurnameSearch}
              className={`w-100 ${surnameSearch.length > 0 && surnameSearch.length < 3 ? "is-invalid" : ""}`}
              style={{
                border: surnameSearch.length > 0 && surnameSearch.length < 3 ? '1px solid red' :
                  surnameSearch.length >= 3 ? '1px solid green' : '1px solid #ccc',
              }}
            />
            {surnameSearch.length > 0 && surnameSearch.length < 3 && (
              <div className="invalid-feedback d-block mb-3">
                {t("Uzvārda meklēšanai nepieciešami vismaz 3 burti")}
              </div>
            )}
            {surnameSearch.length >= 3 && <div className="mb-3"></div>}
            {surnameSearch.length === 0 && <div className="mb-4"></div>}

            {error && <div className="invalid-feedback d-block">{error}</div>}

            {isStaff && (
              <>
                <Form.Control
                  type="text"
                  id="dienestaVienibaSearch"
                  value={dienestaVienibaSearch}
                  onChange={handleDienestaVienibaSearch}
                  placeholder={t("Meklēt pēc dienesta vienības")}
                  title={t("Ieraksti vismaz 3 simbolus, lai meklētu pēc dienesta vienības")}
                  className={`w-100 ${dienestaVienibaSearch.length > 0 && dienestaVienibaSearch.length < 3 ? "is-invalid" : ""}`}
                  style={{
                    border: dienestaVienibaSearch.length > 0 && dienestaVienibaSearch.length < 3
                      ? '1px solid red'
                      : dienestaVienibaSearch.length >= 3
                        ? '1px solid green'
                        : '1px solid #ccc',
                  }}
                />
                {dienestaVienibaSearch.length > 0 && dienestaVienibaSearch.length < 3 && (
                  <div className="invalid-feedback d-block mb-3">
                    {t("Dienesta vienībai jābūt vismaz 3 simboliem")}
                  </div>
                )}
                {dienestaVienibaSearch.length >= 3 && <div className="mb-3"></div>}
                {dienestaVienibaSearch.length === 0 && <div className="mb-4"></div>}
              </>
            )}

            {isBirthdateSearchEnabled && (
              <>
                <Form.Control
                  type="text"
                  id="birthdateSearch"
                  value={birthdateSearch}
                  onChange={handleBirthdateSearch}
                  placeholder={t("Meklēt pēc dzimšanas gada")}
                  title={t("Ieraksti dzimšanas gadu")}
                  className={`w-100 ${birthdateSearch.length > 0 && birthdateSearch.length !== 4 ? "is-invalid" : ""}`}
                  style={{
                    border: birthdateSearch.length > 0 && birthdateSearch.length !== 4
                      ? '1px solid red'
                      : birthdateSearch.length === 4
                        ? '1px solid green'
                        : '1px solid #ccc',
                  }}
                  maxLength={4}
                />
                {birthdateSearch.length > 0 && birthdateSearch.length !== 4 && (
                  <div className="invalid-feedback d-block mb-3">
                    {t("Dzimšanas gadam jāsatur4 cipari")}
                  </div>
                )}
                {birthdateSearch.length === 4 && <div className="mb-3"></div>}
                {birthdateSearch.length === 0 && <div className="mb-4"></div>}
              </>
            )}
          </Form.Group>
          <Button
            onClick={fetchPeople}
            variant="primary"
            type="submit"
            className="w-100 rounded-sm"
            disabled={!(nameSearch.length >= 3 || surnameSearch.length >= 3 || dienestaVienibaSearch.length >= 3 || birthdateSearch.length == 4)}
          >
            <FaSearch className="me-2" style={{ paddingBottom: 3 }} />
            {t("Meklēt")}
          </Button>
        </Form>
        <br></br>

        {
          showLoder &&
          <div className="d-flex justify-content-center py-4">
            <ClipLoader color="#000" loading={true} size={40} />
          </div>
        }

        <ListGroup>
          {displayedPeople.length > 0 && <ListGroup.Item>
            <Row>
              <Col 
                xs={6} 
                md={3} 
                className="text-start text-secondary fw-semibold"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('surname')}
              >
                {t("Vārds, Uzvārds")}
                {getSortIcon('surname')}
              </Col>
              <Col 
                md={3} 
                className="text-start text-secondary fw-semibold d-none d-md-block"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('rank')}
              >
                {t("rank")}
                {getSortIcon('rank')}
              </Col>
              <Col 
                xs={3} 
                md={3} 
                className="text-secondary fw-semibold"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('birthdate')}
              >
                {t("Dzimšanas gads")}
                {getSortIcon('birthdate')}
              </Col>
              <Col 
                xs={3} 
                md={3} 
                className="text-secondary fw-semibold"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('deathdate')}
              >
                {t("Miršanas gads")}
                {getSortIcon('deathdate')}
              </Col>
            </Row>
          </ListGroup.Item>}

          {displayedPeople.map((person, index) => (
            <ListGroup.Item
              key={index}
              action
              onClick={() => {
                setSelectedPerson(person);
                setShowPersonModal(true);
              }}
            >
              <Row className="align-items-center">
                <Col xs={6} md={3} className="fw-bold text-start">
                  {person.name} {person.surname}
                </Col>
                <Col md={3} className="text-start d-none d-md-block">
                  {person.rank || person.first_rank || t('Nav datu')}
                </Col>
                <Col xs={3} md={3}>
                  {(person.birthdate && person.birthdate.replace(/\.$/, '')) || t('Nav datu')}
                </Col>
                <Col xs={3} md={3}>
                  {(
                    person.death_date ||
                    person.other_death_date ||
                    person.dieddate
                  )?.replace(/\.$/, '') || t('Nav datu')}
                </Col>
              </Row>
            </ListGroup.Item>
          ))}
        </ListGroup>

        {
          sortedPeople.length > 20 &&
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
            >
              ← {t('Iepriekšējā')}
            </Button>

            <span className="text-muted">
              {t('Lapa')} {currentPage + 1} / {totalPages}
            </span>

            <Button
              variant="secondary"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))}
              disabled={currentPage >= totalPages - 1}
            >
              {t('Nākamā')} →
            </Button>
          </div>
        }

        <>
          {isSearching && sortedPeople.length > 0 ? (
            <p style={{ paddingTop: '1rem' }}>
              {t("Atrastie rezultāti")}: {sortedPeople.length}{' '}
            </p>
          ) : isSearching && sortedPeople.length === 0 ? (
            <p>{t("Netika atrasts neviens rezultāts")}</p>
          ) : null}
        </>
      </Card >

      {showTopButton && (
        <button className="back-to-top" onClick={scrollToTop}>
          <BsArrowUp size={20} />
        </button>
      )}

      <ErrorModal
        error={error}
        onClose={() => {
          setError(null);
        }}
      />

      {isStaff ?
        <EditPersonModal
          show={showPersonModal}
          setShow={setShowPersonModal}
          selectedPerson={selectedPerson}
          onClose={() => setShowPersonModal(false)}
          onEdited={onEdited}
          onNext={onNextPerson}
          onPrevious={onPreviousPerson}
          hasNext={hasNextPerson}
          hasPrevious={hasPreviousPerson}
        /> : <PersonModal
          show={showPersonModal}
          setShow={setShowPersonModal}
          selectedPerson={selectedPerson}
          onClose={() => setShowPersonModal(false)}
          onNext={onNextPerson}
          onPrevious={onPreviousPerson}
          hasNext={hasNextPerson}
          hasPrevious={hasPreviousPerson}
        />
      }

    </Container >
  );
}